import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  MenuItem,
  IconButton,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  InputAdornment,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  CircularProgress,
  useMediaQuery,
  Card,
  CardContent,
  Paper,
  Alert
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  ArrowBack as ArrowBackIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Receipt as ReceiptIcon,
  ArrowUpward as IncomeIcon,
  ArrowDownward as ExpenseIcon,
  CompareArrows as CompareArrowsIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  FilterAltOff as FilterAltOffIcon,
  CalendarMonth as CalendarIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useSupabase } from '../contexts/SupabaseContext';
import { formatCurrency, formatDate } from '../utils/format';
import { useNavigate, useLocation } from 'react-router-dom';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { fetchAllRows } from '../utils/fetchAllRows';

// PDF generation - using dynamic imports to avoid plugin conflicts

// Helper function to generate statement periods from first transaction date
const generateStatementPeriods = (billingDate, firstTransactionDate = null) => {
  const periods = [];
  const today = dayjs();
  const currentBillingDay = parseInt(billingDate) || 1;

  // If no transactions exist, return empty array
  if (!firstTransactionDate) {
    return periods;
  }

  const firstTxDate = dayjs(firstTransactionDate);

  // Find the first billing period that includes the first transaction
  let startPeriod = firstTxDate.date(currentBillingDay);

  // If the first transaction is before the billing date of that month,
  // the billing period started in the previous month
  if (firstTxDate.date() < currentBillingDay) {
    startPeriod = startPeriod.subtract(1, 'month');
  }

  // Generate periods from the first transaction period to current period
  let currentPeriod = today.date(currentBillingDay);

  // If today is before the billing date, current period started last month
  if (today.date() < currentBillingDay) {
    currentPeriod = currentPeriod.subtract(1, 'month');
  }

  // Generate all periods from start to current
  let periodDate = startPeriod;
  while (periodDate.isSameOrBefore(currentPeriod, 'month')) {
    const periodStart = periodDate;
    const periodEnd = periodStart.add(1, 'month').subtract(1, 'day');
    
    // Label the statement with the month when the bill is generated (end of cycle)
    // This represents the statement for the spending period
    const billGenerationDate = periodStart.add(1, 'month');

    periods.push({
      value: periodStart.format('MMYYYY'),
      label: billGenerationDate.format('MMM YYYY'),
      startDate: periodStart.format('YYYY-MM-DD'),
      endDate: periodEnd.format('YYYY-MM-DD'),
      displayRange: `${periodStart.format('DD MMM')} - ${periodEnd.format('DD MMM YYYY')}`
    });

    periodDate = periodDate.add(1, 'month');
  }

  // Reverse to show most recent first
  return periods.reverse();
};

// Transaction types for filtering
const transactionTypes = [
  { value: 'income', label: 'Income', icon: <IncomeIcon color="success" /> },
  { value: 'expense', label: 'Expense', icon: <ExpenseIcon color="error" /> },
  { value: 'credit', label: 'Credit', icon: <IncomeIcon color="success" /> },
  { value: 'debit', label: 'Debit', icon: <ExpenseIcon color="error" /> },
  { value: 'transfer', label: 'Transfer', icon: <CompareArrowsIcon color="info" /> }
];

const Statements = () => {
  const { supabase, checkConnection } = useSupabase();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();

  // Get card data from navigation state
  const cardData = location.state?.card;

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statementPeriods, setStatementPeriods] = useState([]);
  const [firstTransactionDate, setFirstTransactionDate] = useState(null);
  const [downloadingPDF, setDownloadingPDF] = useState(false);
  const [downloadingCSV, setDownloadingCSV] = useState(false);

  // Generate statement periods after transactions are loaded
  useEffect(() => {
    if (cardData?.billing_date && firstTransactionDate) {
      const periods = generateStatementPeriods(cardData.billing_date, firstTransactionDate);
      setStatementPeriods(periods);
      // Set current period as default
      if (periods.length > 0) {
        setSelectedPeriod(periods[0].value);
      }
    }
  }, [cardData, firstTransactionDate]);

  // Filter transactions based on selected period and other filters
  const filteredTransactions = useMemo(() => {
    if (!transactions.length) return [];

    return transactions.filter(transaction => {
      // Text search
      const matchesSearch = searchQuery
        ? transaction.description.toLowerCase().includes(searchQuery.toLowerCase())
        : true;

      // Type filter
      const matchesType = selectedTypes.length === 0
        ? true
        : selectedTypes.includes(transaction.type);

      // Period filter
      let matchesPeriod = true;
      if (selectedPeriod) {
        const period = statementPeriods.find(p => p.value === selectedPeriod);
        if (period) {
          const transactionDate = dayjs(transaction.date);
          const periodStart = dayjs(period.startDate);
          const periodEnd = dayjs(period.endDate);
          matchesPeriod = transactionDate.isBetween(periodStart, periodEnd, 'day', '[]');
        }
      }

      return matchesSearch && matchesType && matchesPeriod;
    });
  }, [transactions, searchQuery, selectedTypes, selectedPeriod, statementPeriods]);

  // Calculate statement summary with previous balance
  const statementSummary = useMemo(() => {
    let totalDebits = 0;
    let totalCredits = 0;

    filteredTransactions.forEach(t => {
      const amount = Math.abs(t.amount);
      
      // Debits (money spent/going out) - includes expenses, debits, and outgoing transfers
      if ((t.type === 'expense' || t.type === 'debit') && t.amount < 0) {
        totalDebits += amount;
      } else if (t.type === 'transfer' && t.amount < 0) {
        totalDebits += amount;
      }
      // Credits (money received/coming in) - includes income, credits, and incoming transfers
      else if ((t.type === 'income' || t.type === 'credit') && t.amount > 0) {
        totalCredits += amount;
      } else if (t.type === 'transfer' && t.amount > 0) {
        totalCredits += amount;
      }
    });

    // Calculate previous balance (balance before the selected period)
    let previousBalance = 0;
    if (selectedPeriod && statementPeriods.length > 0) {
      const period = statementPeriods.find(p => p.value === selectedPeriod);
      if (period) {
        const periodStart = dayjs(period.startDate);
        
        // Get all transactions before this period
        const transactionsBeforePeriod = transactions.filter(t => {
          const txDate = dayjs(t.date);
          return txDate.isBefore(periodStart, 'day');
        });

        // Calculate previous balance
        transactionsBeforePeriod.forEach(t => {
          const amount = Math.abs(t.amount);
          if ((t.type === 'expense' || t.type === 'debit') && t.amount < 0) {
            previousBalance += amount;
          } else if (t.type === 'transfer' && t.amount < 0) {
            previousBalance += amount;
          } else if ((t.type === 'income' || t.type === 'credit') && t.amount > 0) {
            previousBalance -= amount;
          } else if (t.type === 'transfer' && t.amount > 0) {
            previousBalance -= amount;
          }
        });
      }
    }

    // For credit cards: debits increase the bill, credits reduce it
    const netAmount = totalDebits - totalCredits;
    const closingBalance = previousBalance + netAmount;

    return {
      previousBalance,
      totalDebits,
      totalCredits,
      netAmount,
      closingBalance,
      transactionCount: filteredTransactions.length
    };
  }, [filteredTransactions, selectedPeriod, statementPeriods, transactions]);

  // Sort transactions by date (newest first)
  const sortedTransactions = useMemo(() => {
    return [...filteredTransactions].sort((a, b) => {
      const dateCompare = new Date(b.date) - new Date(a.date);
      if (dateCompare !== 0) return dateCompare;

      // If dates are equal, sort by created_at
      const timeA = new Date(a.created_at).getTime();
      const timeB = new Date(b.created_at).getTime();
      return timeB - timeA;
    });
  }, [filteredTransactions]);

  // Get paginated transactions
  const paginatedTransactions = useMemo(() => {
    return sortedTransactions.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [sortedTransactions, page, rowsPerPage]);

  useEffect(() => {
    const init = async () => {
      if (!cardData) {
        setError('No card data provided');
        setLoading(false);
        return;
      }

      try {
        const isConnected = await checkConnection();
        if (!isConnected) {
          setError('Unable to connect to database');
          return;
        }

        await fetchTransactions();
      } catch (error) {
        console.error('Initialization error:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [cardData]);

  const fetchTransactions = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      // Fetch transactions for the specific card (all pages: a single request
      // stops at 1,000 rows)
      const data = await fetchAllRows(() => supabase
        .from('transactions')
        .select(`
          *,
          wallet:wallets (
            id,
            name,
            type,
            balance
          ),
          category:categories (
            id,
            name,
            type,
            color
          )
        `)
        .eq('user_id', user.id)
        .eq('wallet_id', cardData.id)
        .order('date', { ascending: false })
        .order('id', { ascending: true }));

      const transactionData = data || [];
      setTransactions(transactionData);

      // Find the earliest transaction date
      if (transactionData.length > 0) {
        const sortedByDate = [...transactionData].sort((a, b) => new Date(a.date) - new Date(b.date));
        setFirstTransactionDate(sortedByDate[0].date);
      } else {
        setFirstTransactionDate(null);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setError('Error loading transactions: ' + error.message);
    }
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedTypes([]);
    // Don't clear selected period as it's the main filter
  };

  const handleBackToCards = () => {
    navigate('/credit-cards');
  };

  const handleDownloadPDFStatement = async () => {
    if (!selectedPeriod || filteredTransactions.length === 0) return;

    const selectedPeriodData = statementPeriods.find(p => p.value === selectedPeriod);
    if (!selectedPeriodData) return;

    setDownloadingPDF(true);
    try {
      console.log('Starting PDF generation...');

      // Import only jsPDF, avoid autoTable completely
      const jsPDFModule = await import('jspdf');
      const jsPDF = jsPDFModule.default || jsPDFModule.jsPDF;

      // Create new PDF document
      const doc = new jsPDF();
      console.log('jsPDF instance created successfully');

      const pageWidth = doc.internal.pageSize.width;
      const margin = 20;

      // Header
      console.log('Adding header...');
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      doc.text('Credit Card Statement', margin, 30);

      // Card and period info
      console.log('Adding card info...');
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`Card: ${cardData.name}`, margin, 45);
      doc.text(`Statement Period: ${selectedPeriodData.displayRange}`, margin, 55);
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, margin, 65);

      // Summary section
      console.log('Adding summary section...');
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('Summary', margin, 85);

      doc.setFontSize(11);
      doc.setFont('helvetica', 'normal');

      // Add summary with error handling
      try {
        console.log('Adding summary data...', statementSummary);
        const prevBalLabel = statementSummary.previousBalance >= 0 ? '(Outstanding)' : '(Credit)';
        const closingBalLabel = statementSummary.closingBalance >= 0 ? '(Amount Due)' : '(Credit Balance)';
        
        doc.text(`Previous Balance: ${formatCurrency(Math.abs(statementSummary.previousBalance || 0))} ${prevBalLabel}`, margin, 100);
        doc.text(`Total Debits (Spent): ${formatCurrency(statementSummary.totalDebits || 0)}`, margin, 110);
        doc.text(`Total Credits (Received): ${formatCurrency(statementSummary.totalCredits || 0)}`, margin, 120);
        doc.text(`Closing Balance: ${formatCurrency(Math.abs(statementSummary.closingBalance || 0))} ${closingBalLabel}`, margin, 130);
        doc.text(`Total Transactions: ${statementSummary.transactionCount || 0}`, margin, 140);
        console.log('Summary added successfully');
      } catch (summaryError) {
        console.error('Error adding summary to PDF:', summaryError);
        doc.text('Error generating summary', margin, 100);
      }

      // Prepare table data with error handling
      const tableData = filteredTransactions.map(transaction => {
        try {
          const description = (transaction.description || '').toString();
          const truncatedDescription = description.length > 30
            ? description.substring(0, 30) + '...'
            : description;

          const category = transaction.type === 'transfer'
            ? 'Transfer'
            : (transaction.category?.name || 'Uncategorized');

          const type = transaction.type ?
            transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1) :
            'Unknown';

          const isDebit = (transaction.type === 'expense' || transaction.type === 'debit') && transaction.amount < 0;
          const isTransferOut = transaction.type === 'transfer' && transaction.amount < 0;
          
          const amount = transaction.amount ?
            ((isDebit || isTransferOut) ? '-' : '') + formatCurrency(Math.abs(transaction.amount)) :
            '₹0';

          return [
            transaction.date ? formatDate(transaction.date) : 'N/A',
            truncatedDescription,
            category,
            type,
            amount
          ];
        } catch (err) {
          console.error('Error processing transaction for PDF:', err, transaction);
          return [
            'Error',
            'Error processing transaction',
            'Error',
            'Error',
            '₹0'
          ];
        }
      });

      // Create table - try autoTable first, fallback to simple text table
      console.log('Creating table with data:', tableData.length, 'rows');

      // Validate required variables before creating table
      console.log('Validating variables before table creation...');
      console.log('pageWidth:', pageWidth);
      console.log('margin:', margin);
      console.log('tableData length:', tableData?.length);
      console.log('doc methods available:', typeof doc.text, typeof doc.setFont, typeof doc.setFontSize);

      if (!pageWidth || !margin || !tableData || !Array.isArray(tableData)) {
        throw new Error('Missing required variables for PDF generation');
      }

      // Always use simple text table for now to avoid plugin issues
      console.log('Creating simple text table to avoid plugin issues...');
      createSimpleTextTable();

      function createSimpleTextTable() {
        try {
          let yPosition = 155;

          console.log('Starting simple table creation at Y position:', yPosition);
          console.log('Page width:', pageWidth, 'Margin:', margin);
          console.log('Table data rows:', tableData.length);

          // Add a line separator
          doc.setDrawColor(200, 200, 200);
          doc.line(margin, yPosition - 5, pageWidth - margin, yPosition - 5);

          // Table header
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(25, 118, 210); // Blue color for headers
          doc.text('Date', margin, yPosition);
          doc.text('Description', margin + 30, yPosition);
          doc.text('Category', margin + 80, yPosition);
          doc.text('Type', margin + 120, yPosition);
          doc.text('Amount', margin + 150, yPosition);
          yPosition += 5;

          // Header underline
          doc.setDrawColor(25, 118, 210);
          doc.line(margin, yPosition, pageWidth - margin, yPosition);
          yPosition += 10;

          // Table rows
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);
          doc.setTextColor(0, 0, 0); // Black color for content

          const maxRows = Math.min(tableData.length, 25); // Limit to 25 rows to avoid page overflow
          console.log('Will render', maxRows, 'rows');

          for (let i = 0; i < maxRows; i++) {
            const row = tableData[i];
            if (yPosition > 260) {
              console.log('Stopping at row', i, 'due to page limit');
              break; // Stop if we're near the bottom of the page
            }

            // Alternate row background (simulate with light gray text for even rows)
            if (i % 2 === 0) {
              doc.setTextColor(60, 60, 60);
            } else {
              doc.setTextColor(0, 0, 0);
            }

            // Safely render each cell with error handling
            try {
              doc.text(String(row[0] || ''), margin, yPosition);
              doc.text(String(row[1] || '').substring(0, 25), margin + 30, yPosition);
              doc.text(String(row[2] || '').substring(0, 15), margin + 80, yPosition);
              doc.text(String(row[3] || ''), margin + 120, yPosition);

              // Right-align amount
              const amountText = String(row[4] || '');
              const amountWidth = doc.getTextWidth(amountText);
              doc.text(amountText, pageWidth - margin - amountWidth, yPosition);
            } catch (rowError) {
              console.error('Error rendering row', i, ':', rowError);
              doc.text('Error rendering row', margin, yPosition);
            }

            yPosition += 8;
          }

          // Add footer if there are more transactions
          if (tableData.length > maxRows) {
            yPosition += 5;
            doc.setTextColor(100, 100, 100);
            doc.setFontSize(9);
            doc.text(`... and ${tableData.length - maxRows} more transactions`, margin, yPosition);
          }

          console.log('Simple text table created successfully');
        } catch (tableError) {
          console.error('Error in createSimpleTextTable:', tableError);
          // Minimal fallback
          doc.setFontSize(12);
          doc.setTextColor(0, 0, 0);
          doc.text('Error creating transaction table. Please try CSV download.', margin, 150);
        }
      }

      // Generate filename
      const cardName = cardData.name.replace(/[^a-z0-9]/gi, '_');
      const periodLabel = selectedPeriodData.label.replace(/\s+/g, '_');
      const fileName = `${cardName}_Statement_${periodLabel}.pdf`;

      console.log('Saving PDF with filename:', fileName);
      // Save the PDF
      doc.save(fileName);
      console.log('PDF saved successfully');

    } catch (error) {
      console.error('Error generating PDF:', error);
      console.error('Error details:', error.message, error.stack);
      console.error('Error name:', error.name);
      console.error('Full error object:', error);

      alert(`Error generating PDF: ${error.message}. Check console for details. You can try downloading as CSV instead.`);
    } finally {
      setDownloadingPDF(false);
    }
  };

  const handleDownloadHTMLStatement = () => {
    const selectedPeriodData = statementPeriods.find(p => p.value === selectedPeriod);
    if (!selectedPeriodData) return;

    // Create HTML content
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Credit Card Statement - ${cardData.name}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          .header { text-align: center; margin-bottom: 30px; }
          .summary { background: #f5f5f5; padding: 15px; margin: 20px 0; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #1976d2; color: white; }
          .amount { text-align: right; }
          .expense { color: #d32f2f; }
          .income { color: #2e7d32; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Credit Card Statement</h1>
          <h2>${cardData.name}</h2>
          <p>Statement Period: ${selectedPeriodData.displayRange}</p>
          <p>Generated on: ${new Date().toLocaleDateString()}</p>
        </div>
        
        <div class="summary">
          <h3>Summary</h3>
          <p>Previous Balance: ${formatCurrency(Math.abs(statementSummary.previousBalance || 0))} ${statementSummary.previousBalance >= 0 ? '(Outstanding)' : '(Credit)'}</p>
          <p>Total Debits (Spent): ${formatCurrency(statementSummary.totalDebits || 0)}</p>
          <p>Total Credits (Received): ${formatCurrency(statementSummary.totalCredits || 0)}</p>
          <p>Closing Balance: ${formatCurrency(Math.abs(statementSummary.closingBalance || 0))} ${statementSummary.closingBalance >= 0 ? '(Amount Due)' : '(Credit Balance)'}</p>
          <p>Total Transactions: ${statementSummary.transactionCount || 0}</p>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Description</th>
              <th>Category</th>
              <th>Type</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            ${filteredTransactions.map(transaction => {
              const isDebit = (transaction.type === 'expense' || transaction.type === 'debit') && transaction.amount < 0;
              const isTransferOut = transaction.type === 'transfer' && transaction.amount < 0;
              const displayType = isDebit || isTransferOut ? 'expense' : 'income';
              
              return `
              <tr>
                <td>${formatDate(transaction.date)}</td>
                <td>${transaction.description || ''}</td>
                <td>${transaction.type === 'transfer' ? 'Transfer' : (transaction.category?.name || 'Uncategorized')}</td>
                <td>${transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1)}</td>
                <td class="amount ${displayType}">${(isDebit || isTransferOut) ? '-' : ''}${formatCurrency(Math.abs(transaction.amount))}</td>
              </tr>
              `;
            }).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `;

    // Create blob and download
    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${cardData.name.replace(/[^a-z0-9]/gi, '_')}_Statement_${selectedPeriodData.label.replace(/\s+/g, '_')}.html`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownloadStatement = () => {
    if (!selectedPeriod || filteredTransactions.length === 0) return;

    const selectedPeriodData = statementPeriods.find(p => p.value === selectedPeriod);
    if (!selectedPeriodData) return;

    setDownloadingCSV(true);

    // Prepare CSV data
    const csvHeaders = [
      'Date',
      'Description',
      'Category',
      'Type',
      'Amount',
      'Notes'
    ];

    const csvData = filteredTransactions.map(transaction => {
      const amount = Math.abs(transaction.amount);
      const isDebit = (transaction.type === 'expense' || transaction.type === 'debit') && transaction.amount < 0;
      const isCredit = (transaction.type === 'income' || transaction.type === 'credit') && transaction.amount > 0;
      const isTransferOut = transaction.type === 'transfer' && transaction.amount < 0;
      
      return [
        formatDate(transaction.date),
        `"${transaction.description.replace(/"/g, '""')}"`, // Escape quotes in description
        transaction.type === 'transfer' ? 'Transfer' : (transaction.category?.name || 'Uncategorized'),
        transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1),
        (isDebit || isTransferOut) ? `-${amount}` : amount,
        transaction.notes ? `"${transaction.notes.replace(/"/g, '""')}"` : ''
      ];
    });

    // Create CSV content
    const csvContent = [
      // Header with card and period info
      `Credit Card Statement - ${cardData.name}`,
      `Statement Period: ${selectedPeriodData.displayRange}`,
      `Generated on: ${new Date().toLocaleDateString()}`,
      '',
      // Summary
      `Previous Balance: ${formatCurrency(Math.abs(statementSummary.previousBalance))} ${statementSummary.previousBalance >= 0 ? '(Outstanding)' : '(Credit)'}`,
      `Total Debits (Spent): ${formatCurrency(statementSummary.totalDebits)}`,
      `Total Credits (Received): ${formatCurrency(statementSummary.totalCredits)}`,
      `Closing Balance: ${formatCurrency(Math.abs(statementSummary.closingBalance))} ${statementSummary.closingBalance >= 0 ? '(Amount Due)' : '(Credit Balance)'}`,
      `Total Transactions: ${statementSummary.transactionCount}`,
      '',
      // Transaction headers
      csvHeaders.join(','),
      // Transaction data
      ...csvData.map(row => row.join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `${cardData.name.replace(/[^a-z0-9]/gi, '_')}_Statement_${selectedPeriodData.label.replace(/\s+/g, '_')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }

    setDownloadingCSV(false);
  };

  if (!cardData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          No card data provided. Please go back to credit cards page.
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToCards}
          sx={{ mt: 2 }}
        >
          Back to Credit Cards
        </Button>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Error: {error}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToCards}
          sx={{ mt: 2 }}
        >
          Back to Credit Cards
        </Button>
      </Box>
    );
  }

  const selectedPeriodData = statementPeriods.find(p => p.value === selectedPeriod);

  return (
    <Box sx={{
      p: { xs: 1, sm: 2, md: 4 },
      minHeight: '100vh'
    }}>
      {/* Header Section */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'center' },
        mb: { xs: 2, sm: 3 }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={handleBackToCards}
            sx={{
              bgcolor: 'background.paper',
              boxShadow: 1,
              '&:hover': { bgcolor: 'action.hover' }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                fontSize: { xs: '1.5rem', sm: '2rem' },
                color: 'text.primary',
              }}
            >
              Card Statements
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {cardData.name}
            </Typography>
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ mt: { xs: 2, sm: 0 }, display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={downloadingCSV ? <CircularProgress size={20} /> : <DownloadIcon />}
            onClick={handleDownloadStatement}
            disabled={!selectedPeriod || filteredTransactions.length === 0 || downloadingCSV}
            sx={{
              borderRadius: 2,
              textTransform: 'none'
            }}
          >
            {downloadingCSV ? 'Generating...' : 'Download CSV'}
          </Button>
          <Button
            variant="contained"
            startIcon={downloadingPDF ? <CircularProgress size={20} /> : <DownloadIcon />}
            onClick={handleDownloadPDFStatement}
            disabled={!selectedPeriod || filteredTransactions.length === 0 || downloadingPDF}
            sx={{
              borderRadius: 2,
              textTransform: 'none'
            }}
          >
            {downloadingPDF ? 'Generating...' : 'Download PDF'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadHTMLStatement}
            disabled={!selectedPeriod || filteredTransactions.length === 0}
            sx={{
              borderRadius: 2,
              textTransform: 'none'
            }}
          >
            Download HTML
          </Button>
        </Box>
      </Box>

      {/* Statement Period Selection */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Statement Period</InputLabel>
            <Select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              label="Statement Period"
              startAdornment={<CalendarIcon sx={{ mr: 1, color: 'action.active' }} />}
              disabled={statementPeriods.length === 0}
            >
              {statementPeriods.length === 0 ? (
                <MenuItem disabled>
                  <Typography variant="body2" color="text.secondary">
                    No transactions found
                  </Typography>
                </MenuItem>
              ) : (
                statementPeriods.map((period) => (
                  <MenuItem key={period.value} value={period.value}>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {period.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {period.displayRange}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* No Transactions Message */}
      {transactions.length === 0 && (
        <Paper sx={{
          p: 4,
          mb: 3,
          borderRadius: '16px',
          background: theme => alpha(theme.palette.info.main, 0.1),
          border: theme => `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
          textAlign: 'center'
        }}>
          <Typography variant="h6" sx={{ mb: 1, color: 'info.main' }}>
            No Transactions Found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This credit card doesn't have any transactions yet. Statements will appear here once you start using the card.
          </Typography>
        </Paper>
      )}

      {/* Statement Summary Cards */}
      {transactions.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{
              borderRadius: '16px',
              background: theme => alpha(theme.palette.background.paper, 0.6),
              backdropFilter: 'blur(10px)',
              boxShadow: 'var(--fx-card-shadow)'
            }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Previous Balance
                </Typography>
                <Typography variant="h5" component="div" sx={{ 
                  fontWeight: 600, 
                  color: statementSummary.previousBalance >= 0 ? 'error.main' : 'success.main' 
                }}>
                  {formatCurrency(Math.abs(statementSummary.previousBalance))}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {statementSummary.previousBalance >= 0 ? 'Outstanding' : 'Credit'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{
              borderRadius: '16px',
              background: theme => alpha(theme.palette.background.paper, 0.6),
              backdropFilter: 'blur(10px)',
              boxShadow: 'var(--fx-card-shadow)'
            }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Debits (Spent)
                </Typography>
                <Typography variant="h5" component="div" sx={{ fontWeight: 600, color: 'error.main' }}>
                  {formatCurrency(statementSummary.totalDebits)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Expenses & transfers out
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{
              borderRadius: '16px',
              background: theme => alpha(theme.palette.background.paper, 0.6),
              backdropFilter: 'blur(10px)',
              boxShadow: 'var(--fx-card-shadow)'
            }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Credits (Received)
                </Typography>
                <Typography variant="h5" component="div" sx={{ fontWeight: 600, color: 'success.main' }}>
                  {formatCurrency(statementSummary.totalCredits)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Income & transfers in
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{
              borderRadius: '16px',
              background: theme => alpha(theme.palette.background.paper, 0.6),
              backdropFilter: 'blur(10px)',
              boxShadow: 'var(--fx-card-shadow)'
            }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Closing Balance
                </Typography>
                <Typography
                  variant="h5"
                  component="div"
                  sx={{
                    fontWeight: 600,
                    color: statementSummary.closingBalance >= 0 ? 'error.main' : 'success.main'
                  }}
                >
                  {formatCurrency(Math.abs(statementSummary.closingBalance))}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {statementSummary.closingBalance >= 0 ? 'Amount Due' : 'Credit Balance'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters Section */}
      {transactions.length > 0 && (
        <Paper sx={{
          p: 2,
          mb: 3,
          borderRadius: '16px',
          background: theme => alpha(theme.palette.background.paper, 0.6),
          backdropFilter: 'blur(10px)'
        }}>
          <Grid container spacing={2} alignItems="center">
            {/* Search */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                placeholder="Search transactions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery('')}>
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: 'background.paper',
                    borderRadius: 2
                  }
                }}
              />
            </Grid>

            {/* Transaction Type Filter */}
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Transaction Type</InputLabel>
                <Select
                  multiple
                  value={selectedTypes}
                  onChange={(e) => setSelectedTypes(e.target.value)}
                  input={<OutlinedInput label="Transaction Type" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip
                          key={value}
                          label={value.charAt(0).toUpperCase() + value.slice(1)}
                          size="small"
                        />
                      ))}
                    </Box>
                  )}
                  sx={{ bgcolor: 'background.paper' }}
                >
                  {transactionTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {type.icon}
                        {type.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Clear Filters */}
            <Grid item xs={12} sm={2}>
              {(searchQuery || selectedTypes.length > 0) && (
                <Button
                  variant="outlined"
                  startIcon={<FilterAltOffIcon />}
                  onClick={handleClearFilters}
                  size="small"
                  fullWidth
                >
                  Clear Filters
                </Button>
              )}
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Statement Period Info */}
      {transactions.length > 0 && selectedPeriodData && (
        <Paper sx={{
          p: 2,
          mb: 3,
          borderRadius: '16px',
          background: theme => alpha(theme.palette.info.main, 0.1),
          border: theme => `1px solid ${alpha(theme.palette.info.main, 0.2)}`
        }}>
          <Typography variant="h6" sx={{ mb: 1, color: 'info.main' }}>
            Statement Period: {selectedPeriodData.label}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedPeriodData.displayRange}
          </Typography>
        </Paper>
      )}

      {/* Transactions Table */}
      {transactions.length > 0 && (
        <Paper sx={{
          p: { xs: 1, sm: 2 },
          borderRadius: '16px',
          background: theme => alpha(theme.palette.background.paper, 0.4),
          backdropFilter: 'blur(10px)'
        }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Transaction Details
          </Typography>
          <TableContainer>
            <Table sx={{
              '& .MuiTableCell-root': {
                borderBottom: theme => `1px solid ${alpha(theme.palette.divider, 0.1)}`
              }
            }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedTransactions.map((transaction) => (
                  <TableRow
                    key={transaction.id}
                    sx={{
                      '&:hover': {
                        backgroundColor: theme => alpha(theme.palette.action.hover, 0.1)
                      }
                    }}
                  >
                    <TableCell>
                      {formatDate(transaction.date)}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {transaction.description}
                      </Typography>
                      {transaction.notes && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {transaction.notes}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {transaction.type === 'transfer' ? (
                        <Chip
                          label="Transfer"
                          size="small"
                          icon={<CompareArrowsIcon />}
                          sx={{
                            bgcolor: theme => alpha(theme.palette.info.main, 0.1),
                            color: 'info.main'
                          }}
                        />
                      ) : (
                        <Chip
                          label={transaction.category?.name || 'Uncategorized'}
                          size="small"
                          sx={{
                            bgcolor: transaction.category?.color
                              ? alpha(transaction.category.color, 0.1)
                              : alpha('#666', 0.1),
                            color: transaction.category?.color || '#666'
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1)}
                        size="small"
                        color={
                          transaction.type === 'income' ? 'success' :
                            transaction.type === 'expense' ? 'error' : 'info'
                        }
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        sx={{
                          color: ((transaction.type === 'income' || transaction.type === 'credit') && transaction.amount > 0)
                            ? 'success.main'
                            : ((transaction.type === 'expense' || transaction.type === 'debit') && transaction.amount < 0)
                              ? 'error.main'
                              : transaction.type === 'transfer' && transaction.amount < 0
                                ? 'error.main'
                                : transaction.type === 'transfer' && transaction.amount > 0
                                  ? 'success.main'
                                  : 'info.main',
                          fontWeight: 500
                        }}
                      >
                        {((transaction.type === 'expense' || transaction.type === 'debit') && transaction.amount < 0) || 
                         (transaction.type === 'transfer' && transaction.amount < 0) ? '-' : ''}
                        {formatCurrency(Math.abs(transaction.amount))}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
                {paginatedTransactions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">
                        No transactions found for the selected period
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={sortedTransactions.length}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              borderTop: theme => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              '.MuiTablePagination-select': {
                background: theme => alpha(theme.palette.background.paper, 0.5),
                backdropFilter: 'blur(10px)',
                borderRadius: 1,
                border: theme => `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }
            }}
          />
        </Paper>
      )}
    </Box>
  );
};

export default Statements;